import { tokenStorage } from '@/platform/auth-runtime/token-storage';
import { axiosInstance } from '@/shared/lib/http/instance';
import { signLogBody } from '@/shared/lib/http/request-ticket';
import { createReporter, type Credentials } from '@celerdata/error-reporter';

import type * as IdentityStoreModule from '@/platform/identity/store';

export { sanitizeRequestData } from '@celerdata/error-reporter';

// useUserStore is loaded lazily to break the cycle frontend-alert ↔
// identity/store ↔ identity/api ↔ shared/lib/http/endpoint ↔ axios ↔
// frontend-alert. Static import would resolve axios while it's still
// initializing, producing "endpoint is not a function".
type UserStoreModule = typeof IdentityStoreModule;
let userStoreLoader: Promise<UserStoreModule> | null = null;
let cachedUserStoreModule: UserStoreModule | null = null;
function preloadUserStore(): void {
  if (userStoreLoader || cachedUserStoreModule) return;
  userStoreLoader = import('@/platform/identity/store').then((mod) => {
    cachedUserStoreModule = mod;
    return mod;
  });
}

function readUser(): { userId: string; accountId: string; userRole: string } {
  // Telemetry boots before pinia is ready on some paths (a window error during early
  // bootstrap, or the store module not yet resolved).
  preloadUserStore();
  if (!cachedUserStoreModule) return { userId: '', accountId: '', userRole: '' };
  try {
    const info = cachedUserStoreModule.useUserStore().info ?? {};
    return {
      userId: (info as { userID?: string }).userID ?? '',
      accountId: (info as { accountID?: string }).accountID ?? '',
      userRole: (info as { role?: string }).role ?? '',
    };
  } catch {
    return { userId: '', accountId: '', userRole: '' };
  }
}

// Errors from loading the user (a failing /account/user-info) come before the store holds it,
// and that failure then signs out; the stored account id is still there when they are captured.
// Signed out, the id is only kept to prefill the login page, and may not be the one signing in.
function storedAccountId(): string {
  try {
    return tokenStorage.read()?.aid ?? '';
  } catch {
    return '';
  }
}

function currentCredentials(): Credentials | null {
  const auth = tokenStorage.read();
  if (!auth) return null;
  return { token: auth.token, ubid: auth.ubid, ...(auth.aid ? { accountId: auth.aid } : {}) };
}

const reporter = createReporter({
  logUrl: () => `${axiosInstance.defaults.baseURL ?? ''}/log`,
  context: () => {
    const user = readUser();
    return {
      accountId: user.accountId || storedAccountId(),
      userId: user.userId,
      userRole: user.userRole,
    };
  },
  credentials: currentCredentials,
  sign: (body, credentials) =>
    signLogBody(body, {
      token: credentials.token,
      ubid: credentials.ubid ?? '',
      aid: credentials.accountId ?? '',
    }),
  environment: import.meta.env.MODE ?? 'unknown',
});

export function init(): void {
  preloadUserStore();
  reporter.start();
}

// Wired from main.ts into app.config.errorHandler — Vue render / lifecycle
// errors don't reach window.onerror; this gives a real stack + component info.
export const captureVueError = reporter.captureVueError;

export const reportManualError = reporter.reportManualError;

// Axios interceptors call this for slow requests, API non-success codes, network errors and
// interceptor failures; their messages already end with requestTarget().
export function reportNetworkError(
  type:
    | 'slow_network_request'
    | 'api_response_error'
    | 'network_request_error'
    | 'request_interceptor_error',
  message: string,
  extra: Record<string, unknown> = {},
): void {
  reporter.reportManualError(type, message, extra);
}

// Test-only hook: forgets what was queued and removes the listeners so init() re-registers.
export function __resetForTests(): void {
  reporter.stop();
  userStoreLoader = null;
  cachedUserStoreModule = null;
}

// Test-only: await the lazy `import('@/platform/identity/store')` chain
// (api.ts → endpoint.ts) before the file's afterEach lets vitest tear
// the env down. Without this, a reportError fired late in a test
// triggers the import after teardown and surfaces as a non-test
// EnvironmentTeardownError that tanks the file's exit code.
export function __waitForUserStorePreloadForTests(): Promise<unknown> {
  return userStoreLoader ?? Promise.resolve();
}
